import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

/**
 * The bundle's button family (README "Controls" + PRODUCT_RULES "Buttons").
 *
 *  primary      solid ink, white label, one per view
 *  outlined     1px #e3e3e1, white, hover #f6f6f4
 *  ghost        no border, muted label, hover #f2f2f0 + ink (utility text actions)
 *  quiet        outlined's twin for a row of actions: ink label, no border in
 *               any state, a #f2f2f0 pill under the cursor. Pair it with
 *               `className="tr-hover-quiet-danger"` for a destructive one.
 *  ghostIcon    28–32px square version of ghost, tooltip via `title`
 *  destructive  transparent, red text, hover #fbe3e0 (never solid)
 *  toolbar      dark selection-toolbar button (#2a2a2d, hover #3a3a3e)
 *  toolbarDanger  same, text #f28b82
 *  toolbarCancel  transparent, #a0a0a4, hover #2a2a2d + white
 *
 * `size` is the height. Padding and font size follow the prototype per height
 * (they are not a linear scale): 30 → 0 10 / 12.5px, 32 → 0 12 / 13px,
 * 34 → 0 14 / 13.5px, 36 → 0 16 / 13.5px, 40 → 14px, radius 8, full width in
 * auth. Pass `px` / `fontSize` to override a one-off.
 */
export type ButtonVariant =
  | 'primary'
  | 'outlined'
  | 'quiet'
  | 'ghost'
  | 'ghostIcon'
  | 'destructive'
  | 'toolbar'
  | 'toolbarDanger'
  | 'toolbarCancel';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: 24 | 26 | 28 | 30 | 32 | 34 | 36 | 40;
  px?: number;
  fontSize?: number;
  radius?: number;
  gap?: number;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
  submit?: boolean;
  children?: ReactNode;
}

const PAD: Record<number, [number, number]> = {
  24: [0, 12],
  26: [8, 12],
  28: [10, 12.5],
  30: [10, 12.5],
  32: [12, 13],
  34: [14, 13.5],
  36: [16, 13.5],
  40: [16, 14],
};

export function Button({
  variant = 'outlined',
  size = 32,
  px,
  fontSize,
  radius,
  gap = 7,
  icon,
  iconRight,
  block,
  submit,
  style,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const [dpx, dfs] = PAD[size] ?? [12, 13];
  const base: CSSProperties = {
    display: block ? 'flex' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap,
    height: size,
    padding: `0 ${px ?? dpx}px`,
    border: 0,
    borderRadius: radius ?? (size === 40 ? 8 : size <= 28 ? 6 : 7),
    fontSize: fontSize ?? dfs,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    width: block ? '100%' : undefined,
    flex: 'none',
    lineHeight: 1,
  };
  let v: CSSProperties = {};
  let hover = '';
  switch (variant) {
    case 'primary':
      v = disabled ? { background: 'var(--tr-disabled)', color: 'var(--tr-hint)' } : { background: 'var(--tr-ink)', color: 'var(--tr-on-ink)' };
      hover = disabled ? '' : 'tr-hover-ink2';
      break;
    case 'outlined':
      v = { border: '1px solid var(--tr-line-2)', background: 'var(--tr-surface)', color: 'var(--tr-ink)' };
      hover = 'tr-hover-soft';
      break;
    case 'quiet':
      v = { background: 'transparent', color: 'var(--tr-ink)' };
      hover = 'tr-hover-quiet';
      break;
    case 'ghost':
      v = { background: 'transparent', color: 'var(--tr-muted)', borderRadius: radius ?? 6 };
      hover = 'tr-hover-track-ink';
      break;
    case 'ghostIcon':
      v = {
        background: 'transparent',
        color: 'var(--tr-muted)',
        width: size,
        padding: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: radius ?? (size >= 32 ? 7 : 6),
      };
      hover = 'tr-hover-track-ink';
      break;
    case 'destructive':
      v = { background: 'transparent', color: 'var(--tr-danger)', borderRadius: radius ?? 6 };
      hover = 'tr-hover-danger';
      break;
    case 'toolbar':
      v = { background: '#2a2a2d', color: '#fff', borderRadius: 8 };
      hover = 'tr-hover-toolbar';
      break;
    case 'toolbarDanger':
      v = { background: '#2a2a2d', color: '#f28b82', borderRadius: 8 };
      hover = 'tr-hover-toolbar';
      break;
    case 'toolbarCancel':
      v = { background: 'transparent', color: '#a0a0a4', borderRadius: 8, gap: 6 };
      hover = 'tr-hover-toolbar-cancel';
      break;
  }
  return (
    <button
      type={submit ? 'submit' : 'button'}
      disabled={disabled}
      className={[hover, className].filter(Boolean).join(' ')}
      style={{ ...base, ...v, ...style }}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

/**
 * A link-shaped action: the prototype's `<a href="#">` text actions
 * (See all →, Mark all as read, Retry, Make default, Manage…).
 */
export function TextLink({
  children,
  onClick,
  color = 'var(--tr-ink)',
  fontSize = 12.5,
  weight = 500,
  underline,
  hover = 'ink-underline',
  style,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  color?: string;
  fontSize?: number;
  weight?: number;
  underline?: boolean;
  hover?: 'underline' | 'ink-underline' | 'ink' | 'none';
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <a
      href="#"
      title={title}
      className={hover === 'none' ? undefined : `tr-hover-${hover}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      style={{
        color,
        fontSize,
        fontWeight: weight,
        textDecoration: underline ? 'underline' : 'none',
        textUnderlineOffset: underline ? 2 : undefined,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </a>
  );
}
